
import React from 'react';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ResponsiveTable } from '@/components/ui/responsive-table';
import { useTranslation } from 'react-i18next';
import AppointmentTableRow from './AppointmentTableRow';
import { Appointment } from '../types/appointmentTypes';

interface AppointmentHistoryTableProps {
  appointments: Appointment[];
  onRefundRequest: (appointmentId: string) => void;
  onDeleteAppointment: (appointmentId: string) => void;
  unreadByBooking?: Record<string, number>;
  userTimezone?: string;
}

export const AppointmentHistoryTable: React.FC<AppointmentHistoryTableProps> = ({
  appointments,
  onRefundRequest,
  onDeleteAppointment,
  unreadByBooking = {},
  userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
}) => {
  const { t } = useTranslation('dashboard');

  const canRequestRefund = (appointment: Appointment) => {
    const now = new Date();
    const appointmentDate = new Date(appointment.start_time);
    const daysDifference = Math.ceil((now.getTime() - appointmentDate.getTime()) / (1000 * 3600 * 24));
    
    return (
      appointment.status === 'Completed' && 
      appointment.payment_status === 'Paid' &&
      daysDifference <= 30 &&
      !appointment.refund_status
    );
  };

  const canDelete = (appointment: Appointment) => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    return (
      appointment.status === 'Cancelled' && 
      new Date(appointment.start_time) >= thirtyDaysAgo
    );
  };

  return (
    <ResponsiveTable className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="min-w-[100px]">{t('appointments.table.date')}</TableHead>
            <TableHead className="min-w-[80px]">{t('appointments.table.time')}</TableHead>
            <TableHead className="min-w-[120px]">{t('appointments.table.practitioner')}</TableHead>
            <TableHead className="min-w-[100px]">{t('appointments.table.service')}</TableHead>
            <TableHead className="min-w-[90px]">{t('appointments.table.status')}</TableHead>
            <TableHead className="min-w-[120px]">{t('appointments.table.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {appointments.map((appointment) => (
            <AppointmentTableRow
              key={appointment.id}
              appointment={appointment}
              onRefundRequest={onRefundRequest}
              onDeleteAppointment={() => onDeleteAppointment(appointment.id)}
              canRequestRefund={canRequestRefund(appointment)}
              canDelete={canDelete(appointment)}
              userType="client"
              unreadCount={unreadByBooking[appointment.id] || 0}
              userTimezone={userTimezone}
            />
          ))}
        </TableBody>
      </Table>
    </ResponsiveTable>
  );
};

export default AppointmentHistoryTable;
